import { cn } from '@/app/utils/cn';
import Image from 'next/image';
import { useState, useMemo } from 'react';

const getOptionValue = (option) => {
    return typeof option === 'string' ? option : option.value || option;
};

const getOptionLabel = (option) => {
    return typeof option === 'string'
        ? option
        : option.label || option.name || getOptionValue(option);
};

const isOptionSelected = (option, selectedOptions, multiselect, active, children) => {
    const optionValue = getOptionValue(option);

    if (multiselect) {
        return selectedOptions.some((selected) => {
            const selectedValue = getOptionValue(selected);
            return selectedValue === optionValue;
        });
    }

    return active && optionValue === children;
};

function CategoryButton({
    children,
    onClick = () => {},
    active = false,
    className = '',
    activeClassName = '',
    multiselect = false,
    options = [],
    selectedOptions = [],
    onSelectionChange = () => {},
    searchPlaceholder = 'Search...',
    enableSmartSearch = false,
    defaultText = '',
    maxDisplayItems = 2
}) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const isInputMode = multiselect && enableSmartSearch && isDropdownOpen;

    const filteredOptions = useMemo(() => {
        if (!enableSmartSearch || !searchTerm.trim()) {
            return options;
        }

        return options.filter((option) => {
            const optionText = getOptionLabel(option);
            return optionText.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [options, searchTerm, enableSmartSearch]);

    const getDisplayText = () => {
        if (!multiselect) {
            return children;
        }

        if (selectedOptions.length === 0) {
            return defaultText || children;
        }

        if (selectedOptions.length <= maxDisplayItems) {
            const labels = selectedOptions.map((option) => getOptionLabel(option));
            return labels.join(', ');
        }

        const firstItems = selectedOptions
            .slice(0, maxDisplayItems)
            .map((option) => getOptionLabel(option));
        const remainingCount = selectedOptions.length - maxDisplayItems;
        return `${firstItems.join(', ')} and ${remainingCount}`;
    };

    const handleOptionSelect = (option) => {
        if (!multiselect) {
            onClick(option);
            onSelectionChange([option]);
            setIsDropdownOpen(false);
            return;
        }

        const optionValue = getOptionValue(option);
        const isSelected = selectedOptions.some((selected) => {
            const selectedValue = getOptionValue(selected);
            return selectedValue === optionValue;
        });

        let newSelection;
        if (isSelected) {
            newSelection = selectedOptions.filter((selected) => {
                const selectedValue = getOptionValue(selected);
                return selectedValue !== optionValue;
            });
        } else {
            newSelection = [...selectedOptions, option];
        }

        onSelectionChange(newSelection);
    };

    if (!multiselect && options.length === 0) {
        return (
            <button
                onClick={onClick}
                className={cn(
                    'px-4 py-2 rounded-lg text-base font-medium w-fit md:w-48',
                    active
                        ? activeClassName || 'bg-fuschia-700 text-white'
                        : 'bg-blue-100 text-blue-700',
                    'hover:bg-fuschia-700 hover:text-white',
                    className
                )}>
                {children}
            </button>
        );
    }

    return (
        <div className="relative">
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={cn(
                    'group flex max-h-[42px] md:max-w-[325px] w-full items-center gap-2 rounded-lg px-4 py-2 text-base font-medium',
                    multiselect
                        ? selectedOptions.length > 0
                            ? activeClassName || 'bg-fuschia-700 text-white'
                            : 'bg-blue-100 text-blue-700'
                        : active
                          ? activeClassName || 'bg-fuschia-700 text-white'
                          : 'bg-blue-100 text-blue-700',
                    'hover:bg-fuschia-700 hover:text-white',
                    className
                )}>
                {isInputMode ? (
                    <div className="relative flex-1">
                        <input
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            className="w-full rounded-md border border-white bg-white px-2 py-1 pr-7 text-sm font-normal text-black placeholder:text-black focus:outline-none focus:ring-0"
                        />
                        {multiselect && selectedOptions.length > 0 && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectionChange([]);
                                    setIsDropdownOpen(false);
                                }}
                                className="absolute right-2 top-1/2 z-50 -translate-y-1/2 text-gray-500 hover:cursor-pointer hover:text-gray-700"
                                aria-label="Clear all">
                                ×
                            </button>
                        )}
                    </div>
                ) : (
                    <span className="truncate">{getDisplayText()}</span>
                )}

                {!isInputMode && (
                    <Image
                        src="/assets/icons/chevron-down.svg"
                        alt={isDropdownOpen ? 'Close dropdown menu' : 'Open dropdown menu'}
                        width={16}
                        height={16}
                        className={cn(
                            'size-4 transition-transform invert',
                            'group-hover:invert',
                            isDropdownOpen && 'rotate-180'
                        )}
                        unoptimized
                    />
                )}
                {multiselect && selectedOptions.length > 1 && (
                    <span className="rounded-[5px] bg-white px-2 py-[7px] text-[11px] font-bold text-black">
                        {selectedOptions.length}
                    </span>
                )}
            </button>

            {isDropdownOpen && (
                <div className="absolute left-0 top-full z-50 mt-2 !min-w-full rounded-lg border border-gray-200 bg-white shadow-lg md:min-w-[325px]">
                    {enableSmartSearch && !isInputMode && (
                        <div className="relative z-[100] border-b border-gray-200 p-3">
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fuschia-500"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    <div className="relative z-50 max-h-60 !min-w-full overflow-y-auto sm:w-full md:min-w-[325px]">
                        {filteredOptions.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500">
                                {searchTerm ? 'Nothing found' : 'No options'}
                            </div>
                        ) : (
                            filteredOptions.map((option, index) => {
                                const optionLabel = getOptionLabel(option);
                                const isSelected = isOptionSelected(
                                    option,
                                    selectedOptions,
                                    multiselect,
                                    active,
                                    children
                                );

                                return (
                                    <button
                                        key={index}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOptionSelect(option);
                                        }}
                                        className={cn(
                                            'flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-100',
                                            isSelected && 'bg-fuschia-50 text-fuschia-700'
                                        )}>
                                        <span>{optionLabel}</span>
                                        {isSelected && (
                                            <Image
                                                src="/assets/icons/check.svg"
                                                alt="Selected"
                                                width={16}
                                                height={16}
                                                className="size-4"
                                                unoptimized
                                            />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {isDropdownOpen && (
                <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
            )}
        </div>
    );
}

export default CategoryButton;
